import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Modal from "react-modal";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthGuard } from "../App";
import { Oval } from "react-loader-spinner"; // Import loader
import { RoleGuard } from "../App";
import { StandardURL } from "../App";

const TrashUnitsPage = () => {
  const [trashFee, setTrashFee] = useState({
    amount: null,
  }); // State for trash fee as an object
  const [showAddModal, setShowAddModal] = useState(false); // State for showing add modal
  const [amount, setAmount] = useState(null); // State for amount in modal
  const [isLoading, setIsLoading] = useState(false); // Add loading state
  const navigate = useNavigate();
  const propertyId = localStorage.getItem("selectedPropertyId");

  useEffect(() => {
    const token = localStorage.getItem("token");

    if (propertyId) {
      setIsLoading(true); // Start loading before making the API call
      axios
        .get(
          `${StandardURL}/iNyumba/trashfee/fetchonetrashfee/${propertyId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
        .then((response) => {
          if (response.data.response) {
            setTrashFee(response.data.response); // Set trash fee data if available
            toast.success("Trash fee data fetched successfully!");
          } else {
            setTrashFee({}); // If no data, clear the trashFee state
          }
          setIsLoading(false);
        })
        .catch((error) => {
          toast.error(
            error.response?.data?.response || "Failed to fetch trash fee data."
          );
          console.error("Error fetching trash fee:", error);
          setIsLoading(false); // Stop loading on error
        });
    }
  }, [propertyId]); // Trigger the effect whenever propertyId changes

  const handleAddTrashFee = () => {
    setIsLoading(true); // Set loading state to true
    // Create FormData object
    const formData = new FormData();
    formData.append("feeamount", amount);

    const token = localStorage.getItem("token");

    // Send POST request to add trash fee
    axios
      .post(
        `${StandardURL}/iNyumba/trashfee/posttrashfee/${propertyId}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((response) => {
        toast.success(response.data.response);
        setIsLoading(false); // Set loading state to false
        setShowAddModal(false); // Close the modal after adding trash fee
      })
      .catch((error) => {
        toast.error(error.response.data.response);
        console.error("Error adding trash fee:", error);
        setIsLoading(false); // Reset loading state in case of error
      });
  };

  const handleUpdateTrashFee = () => {
    localStorage.setItem("propertyIdTrash", JSON.stringify(propertyId));
    navigate("/landlord/trashunitsupdate"); // Navigate to trash fee update page
  };

  const handleBack = () => {
    navigate(-1); // Go back to the previous page
  };

  return (
    <RoleGuard>
      <AuthGuard>
        <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
          <div className="max-w-xl w-full bg-white p-8 rounded-xl shadow-2xl border border-gray-200">
            <h1 className="text-4xl font-extrabold text-gray-900 mb-6 text-center">
              Trash Fee 🗑️
            </h1>
            <hr className="my-6 border-gray-200" />

            {/* Information about setting/updating trash fee */}
            <div className="mb-8 p-4 bg-yellow-50 rounded-lg shadow-sm border border-yellow-200 text-yellow-800">
              <p className="text-sm font-medium">
                <strong className="font-bold">Important:</strong> If a trash fee is already set, please use the update option. Avoid adding duplicate entries.
              </p>
            </div>

            {/* Display Current Trash Fee */}
            <div className="mb-8 p-6 bg-emerald-50 rounded-lg shadow-inner border border-emerald-200">
              <h2 className="text-2xl font-bold text-emerald-800 mb-4">Current Status</h2>
              {trashFee?.feeamount !== undefined && trashFee.feeamount !== null ? (
                <p className="text-lg text-gray-700">
                  <strong className="font-semibold text-gray-800">Current Trash Fee Amount:</strong>{" "}
                  Ksh {trashFee.feeamount.toLocaleString()}
                </p>
              ) : (
                <p className="text-lg text-gray-600">No trash fee data available for this property.</p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap justify-center gap-4 mt-6">
              {/* Update Trash Fee Button - Shown if data exists */}
              {trashFee?.feeamount !== undefined && trashFee.feeamount !== null ? (
                <button
                  onClick={handleUpdateTrashFee}
                  className="flex items-center px-6 py-3 bg-green-600 text-white font-semibold rounded-lg shadow-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition duration-300 ease-in-out"
                  disabled={isLoading} // Disable during any loading operation
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 mr-2"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" />
                    <path
                      fillRule="evenodd"
                      d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Update Trash Fee
                </button>
              ) : (
                // Add Trash Fee Button - Shown if no data exists
                <button
                  onClick={() => setShowAddModal(true)}
                  className="flex items-center px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-300 ease-in-out"
                  disabled={isLoading} // Disable during any loading operation
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 mr-2"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Add Trash Fee
                </button>
              )}

              {/* Back Button */}
              <button
                onClick={handleBack}
                className="flex items-center px-6 py-3 bg-gray-500 text-white font-semibold rounded-lg shadow-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition duration-300 ease-in-out"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-2"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                Back
              </button>
            </div>

            {/* Modal for Adding Trash Fee */}
            {showAddModal && (
              <Modal
                isOpen={showAddModal}
                onRequestClose={() => setShowAddModal(false)}
                className="fixed inset-0 flex items-center justify-center p-4 z-50"
                overlayClassName="fixed inset-0 bg-black bg-opacity-50 z-40"
              >
                <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md mx-auto">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-6 text-center">Add Trash Fee 💲</h2>
                  <p className="text-gray-600 text-sm mb-6 p-3 bg-yellow-50 rounded-md border border-yellow-200">
                    <strong className="font-bold">Reminder:</strong> If a trash fee is already recorded, please use the update function instead of creating a new entry.
                  </p>
                  <div className="mb-4">
                    <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-2">
                      Amount:
                    </label>
                    <input
                      type="number"
                      id="amount"
                      className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-150 ease-in-out"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="Enter trash fee amount"
                    />
                  </div>
                  <div className="flex justify-end gap-3 mt-6">
                    <button
                      onClick={handleAddTrashFee}
                      disabled={!amount || isLoading} // Disable if no amount or during loading
                      className={`flex items-center px-5 py-2 rounded-md shadow-sm font-semibold transition duration-300 ${
                        isLoading
                          ? "bg-gray-500 cursor-not-allowed"
                          : "bg-indigo-600 hover:bg-indigo-700 text-white"
                      }`}
                    >
                      {isLoading ? (
                        <Oval
                          height={20}
                          width={20}
                          color="white"
                          visible={true}
                          ariaLabel="loading-indicator"
                          secondaryColor="white"
                        />
                      ) : (
                        <>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5 mr-2"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                              clipRule="evenodd"
                            />
                          </svg>
                          Add
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => setShowAddModal(false)}
                      className="flex-1 sm:flex-none px-5 py-2 bg-gray-300 text-gray-800 font-semibold rounded-md shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition duration-300"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </Modal>
            )}
          </div>
        </div>
      </AuthGuard>
    </RoleGuard>
  );
};

export default TrashUnitsPage;
